import React, { useEffect, useState } from "react";

import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";

import type { AfterServicePolicyCreateRequest, ReturnExchangePolicyCreateRequest, ShippingPolicyCreateRequest } from "api/types/policies.types";
import "./PolicyFormModal.scss";

export type PolicyFormType = "shipping" | "return-exchange" | "after-service";

export type PolicyFormPayload = ShippingPolicyCreateRequest | ReturnExchangePolicyCreateRequest | AfterServicePolicyCreateRequest;

interface PolicyFormModalProps {
  open: boolean;
  type: PolicyFormType;
  submitLabel?: string;
  submitDisabled?: boolean;
  onClose: () => void;
  onSubmit: (payload: PolicyFormPayload) => void;
  // 수정 모드 초기값
  initialShipping?: Partial<ShippingPolicyCreateRequest>;
  initialReturnExchange?: Partial<ReturnExchangePolicyCreateRequest>;
  initialAfterService?: Partial<AfterServicePolicyCreateRequest>;
  initialZonesJson?: string;
}

const PAY_TYPE_OPTIONS = [
  { value: "prepaid", label: "선불" },
  { value: "cod", label: "착불" },
  { value: "mixed", label: "혼합" },
];

const TITLE_MAP: Record<PolicyFormType, string> = {
  shipping: "배송 정책",
  "return-exchange": "반품/교환 정책",
  "after-service": "A/S 정책",
};

const defaultShipping = (): ShippingPolicyCreateRequest => ({
  name: "",
  shipping_enabled: true,
  method: "",
  courier: "",
  bundle_shipping: true,
  base_fee: "0",
  free_threshold: "",
  pay_type: "prepaid",
});

const defaultReturnExchange = (): ReturnExchangePolicyCreateRequest => ({
  name: "",
  carrier: "",
  return_fee_oneway: "0",
  exchange_fee_round: "0",
  address: "",
});

const defaultAfterService = (): AfterServicePolicyCreateRequest => ({
  name: "",
  phone: "",
  guide: "",
  warranty_period: "",
});

const PolicyFormModal: React.FC<PolicyFormModalProps> = ({ open, type, submitLabel = "저장", submitDisabled = false, onClose, onSubmit, initialShipping, initialReturnExchange, initialAfterService, initialZonesJson = "" }) => {
  const [shippingForm, setShippingForm] = useState<ShippingPolicyCreateRequest>(defaultShipping);
  const [returnForm, setReturnForm] = useState<ReturnExchangePolicyCreateRequest>(defaultReturnExchange);
  const [asForm, setAsForm] = useState<AfterServicePolicyCreateRequest>(defaultAfterService);
  const [zonesJson, setZonesJson] = useState("");
  const [zonesError, setZonesError] = useState("");

  // open 될 때마다 초기값으로 리셋
  useEffect(() => {
    if (!open) return;
    setShippingForm({ ...defaultShipping(), ...initialShipping });
    setReturnForm({ ...defaultReturnExchange(), ...initialReturnExchange });
    setAsForm({ ...defaultAfterService(), ...initialAfterService });
    setZonesJson(initialZonesJson);
    setZonesError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleZonesChange = (value: string) => {
    setZonesJson(value);
    if (!value.trim()) {
      setZonesError("");
      return;
    }
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed?.zones)) {
        setZonesError('{"zones": [...]} 형태여야 합니다.');
      } else {
        setZonesError("");
      }
    } catch {
      setZonesError("유효하지 않은 JSON 형식입니다.");
    }
  };

  const handleSubmit = () => {
    if (type === "shipping") {
      if (zonesJson.trim() && zonesError) return;
      const payload: ShippingPolicyCreateRequest = { ...shippingForm };
      if (zonesJson.trim() && !zonesError) {
        payload.region_surcharge = JSON.parse(zonesJson);
      }
      onSubmit(payload);
    } else if (type === "return-exchange") {
      onSubmit(returnForm);
    } else {
      onSubmit(asForm);
    }
  };

  const zonesCount = !zonesError && zonesJson.trim() ? JSON.parse(zonesJson).zones.length : null;

  return (
    <Modal
      open={open}
      title={`${TITLE_MAP[type]} ${submitLabel === "등록" ? "등록" : "수정"}`}
      onClose={onClose}
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitDisabled || (type === "shipping" && !!zonesJson.trim() && !!zonesError)} title={submitDisabled ? "백엔드 API 구현 후 활성화됩니다" : undefined}>
            {submitLabel}
          </Button>
        </>
      }
    >
      {type === "shipping" && (
        <div className="policy-form">
          <Input label="정책명 *" placeholder="예: 일반 배송" value={shippingForm.name} onChange={(e) => setShippingForm((p) => ({ ...p, name: e.target.value }))} />
          <Input label="배송 방법" placeholder="예: 택배, 직접배송, 픽업" value={shippingForm.method ?? ""} onChange={(e) => setShippingForm((p) => ({ ...p, method: e.target.value }))} />
          <Input label="택배사" placeholder="예: CJ대한통운" value={shippingForm.courier ?? ""} onChange={(e) => setShippingForm((p) => ({ ...p, courier: e.target.value }))} />
          <div className="policy-form__row">
            <Input label="기본 배송비 (원) *" type="number" placeholder="0" value={shippingForm.base_fee} onChange={(e) => setShippingForm((p) => ({ ...p, base_fee: e.target.value }))} />
            <Input label="무료 배송 기준금액 (원)" type="number" placeholder="예: 40000" value={shippingForm.free_threshold ?? ""} onChange={(e) => setShippingForm((p) => ({ ...p, free_threshold: e.target.value || null }))} />
          </div>
          <Select
            label="결제 방식"
            value={shippingForm.pay_type ?? "prepaid"}
            onChange={(e) =>
              setShippingForm((p) => ({
                ...p,
                pay_type: e.target.value as "prepaid" | "cod" | "mixed",
              }))
            }
            options={PAY_TYPE_OPTIONS}
          />
          <div className="policy-form__checks">
            <label className="policy-form__check">
              <input type="checkbox" checked={shippingForm.shipping_enabled ?? true} onChange={(e) => setShippingForm((p) => ({ ...p, shipping_enabled: e.target.checked }))} />
              배송 사용
            </label>
            <label className="policy-form__check">
              <input type="checkbox" checked={shippingForm.bundle_shipping ?? true} onChange={(e) => setShippingForm((p) => ({ ...p, bundle_shipping: e.target.checked }))} />
              묶음 배송
            </label>
          </div>
          <div className="policy-form__field">
            <label className="input-label">
              도서산간 추가 배송비 (선택)
              <span className="policy-form__hint">
                {"{"}"zones": [...]{"}"} 형태의 JSON
              </span>
            </label>
            <textarea className={`policy-form__textarea${zonesError ? " policy-form__textarea--error" : ""}`} placeholder={'{"zones": [{"name": "제주도 본섬", "extra_fee": 3000, "match": {"si_do": ["제주특별자치도"]}}]}'} rows={6} value={zonesJson} onChange={(e) => handleZonesChange(e.target.value)} />
            {zonesError && <span className="policy-form__error">{zonesError}</span>}
            {zonesCount !== null && <span className="policy-form__zones-count">{zonesCount}개 권역 입력됨</span>}
          </div>
        </div>
      )}

      {type === "return-exchange" && (
        <div className="policy-form">
          <Input label="정책명 *" placeholder="예: 일반 반품" value={returnForm.name} onChange={(e) => setReturnForm((p) => ({ ...p, name: e.target.value }))} />
          <Input label="반품/교환 택배사" placeholder="예: CJ대한통운" value={returnForm.carrier ?? ""} onChange={(e) => setReturnForm((p) => ({ ...p, carrier: e.target.value }))} />
          <div className="policy-form__row">
            <Input label="반품 배송비 편도 (원) *" type="number" placeholder="0" value={returnForm.return_fee_oneway} onChange={(e) => setReturnForm((p) => ({ ...p, return_fee_oneway: e.target.value }))} />
            <Input label="교환 배송비 왕복 (원) *" type="number" placeholder="0" value={returnForm.exchange_fee_round} onChange={(e) => setReturnForm((p) => ({ ...p, exchange_fee_round: e.target.value }))} />
          </div>
          <Input label="회수지 주소" placeholder="예: 서울특별시 서초구..." value={returnForm.address ?? ""} onChange={(e) => setReturnForm((p) => ({ ...p, address: e.target.value }))} />
        </div>
      )}

      {type === "after-service" && (
        <div className="policy-form">
          <Input label="정책명 *" placeholder="예: 1년 무상 A/S" value={asForm.name} onChange={(e) => setAsForm((p) => ({ ...p, name: e.target.value }))} />
          <Input label="A/S 전화번호" placeholder="예: 010-1234-5678" value={asForm.phone ?? ""} onChange={(e) => setAsForm((p) => ({ ...p, phone: e.target.value }))} />
          <Input label="보증 기간" placeholder="예: 1년, 6개월" value={asForm.warranty_period ?? ""} onChange={(e) => setAsForm((p) => ({ ...p, warranty_period: e.target.value }))} />
          <div className="policy-form__field">
            <label className="input-label">A/S 안내</label>
            <textarea className="policy-form__textarea" placeholder="A/S 처리 절차 및 유의사항" rows={4} value={asForm.guide ?? ""} onChange={(e) => setAsForm((p) => ({ ...p, guide: e.target.value }))} />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PolicyFormModal;
